import { FC, useRef } from 'react';
import { Checkbox, Flex, Typography } from 'antd';
import {
  SDropdown,
  ErrorMessage,
  SLabelTitle,
  SMailSelector,
  ModalDialog,
  StatusDropdown,
  UploadButton,
  SInput,
  STextArea,
  SDatePicker,
} from '../../';
import { IProps } from './type';
import globalStyles from '../../../assets/styles/index.module.scss';
import dayjs from 'dayjs';
import { getValueFromEnum } from '../../../utils/functions';
import { useAppSelector } from '../../../store';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_FORMAT } from '../../../assets/conts';
import { modalTypes } from '../../../assets/enums';
import { useSetOrganizationOnOpen } from '../../../hooks';

dayjs.extend(customParseFormat);

const MotorInsuranceModal: FC<IProps> = (props) => {
  const { getFieldProps, setFieldValue, values, errors, touched } =
    props.formik;
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const { organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );

  const handleChangeCheckbox = (e: CheckboxChangeEvent, field: string) => {
    setFieldValue(
      'branch',
      e.target.checked
        ? [...values.branch, field]
        : values.branch.filter((item) => item !== field),
    );
  };

  useSetOrganizationOnOpen(props.open, values, setFieldValue);

  return (
    <ModalDialog
      title={props.title}
      subTitle={props.subTitle}
      open={props.open}
      handleOk={props.handleSubmit}
      handleCancel={props.handleCancel}
    >
      <Flex gap="middle" vertical>
        <Flex align="flex-start" justify="space-between">
          <SLabelTitle>Şirket Bilgileri</SLabelTitle>
          <Flex vertical gap="small">
            {props.type === modalTypes.CANCEL ||
            props.type === modalTypes.ADDENDUM ? (
              <Typography className={globalStyles.labelValue}>
                {
                  props.organizations.find(
                    (item) => item.id === (values.organization || organization),
                  )?.name
                }
              </Typography>
            ) : (
              <SDropdown
                id="organization"
                value={
                  props.organizations.find(
                    (item) =>
                      item.id ===
                      (values.organization ||
                        (organization !== 1 ? organization : undefined)),
                  )?.name
                }
                placeholder="Şirket Seçiniz"
                className={globalStyles.width200px}
                disabled={organization !== 1}
                options={props.organizations.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                onChange={(value) => setFieldValue('organization', value)}
              />
            )}
          </Flex>
        </Flex>
        {props.type === modalTypes.CREATE ? (
          <Flex gap="small" vertical>
            <SLabelTitle>Poliçe Bilgileri</SLabelTitle>
            <Flex vertical gap="small">
              <Flex justify="space-between">
                <Checkbox
                  checked={values.branch.includes('insurance')}
                  onChange={(e: CheckboxChangeEvent) =>
                    handleChangeCheckbox(e, 'insurance')
                  }
                >
                  Kasko
                </Checkbox>
                <Checkbox
                  checked={values.branch.includes('traffic')}
                  onChange={(e: CheckboxChangeEvent) =>
                    handleChangeCheckbox(e, 'traffic')
                  }
                >
                  Trafik
                </Checkbox>
                <Checkbox
                  checked={values.branch.includes('imm')}
                  onChange={(e: CheckboxChangeEvent) =>
                    handleChangeCheckbox(e, 'imm')
                  }
                >
                  İMM
                </Checkbox>
                <Checkbox
                  checked={values.branch.includes('green_card')}
                  onChange={(e: CheckboxChangeEvent) =>
                    handleChangeCheckbox(e, 'green_card')
                  }
                >
                  Yeşil Kart (Yurtdışı)
                </Checkbox>
              </Flex>
              {touched.branch && errors.branch && (
                <ErrorMessage>{errors.branch}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        ) : (
          <Flex justify="space-between">
            <SLabelTitle>Poliçe Tipi</SLabelTitle>
            <Typography className={globalStyles.labelValue}>
              {getValueFromEnum(props.branches, values.branch[0])}
            </Typography>
          </Flex>
        )}
        <Flex justify="space-between">
          <SLabelTitle>Araç Plakası</SLabelTitle>
          <Flex vertical gap="small" className={globalStyles.widthOneThird}>
            <SInput
              placeholder="Araç Plakası"
              {...getFieldProps('license_plate')}
            />
            {touched.license_plate && errors.license_plate && (
              <ErrorMessage>{errors.license_plate}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <Flex gap="small" vertical>
          {props.type === modalTypes.CANCEL ||
          props.type === modalTypes.ADDENDUM ? (
            <>
              <SLabelTitle>
                {props.type === modalTypes.CANCEL
                  ? 'Gerekli Belgeler'
                  : 'Plaka Değişiklik Zeyili'}
              </SLabelTitle>
              <Typography className={globalStyles.documentDescription}>
                {props.type === modalTypes.CANCEL
                  ? '* Oto sigortaları sadece aracın satılması halinde bitiş tarihinden önce revize edilebilmektedir. Poliçe iptali için lütfen noter satış belgesini yükleyiniz. Satılan araca ait kasko ve trafik sigortası poliçelerinizin iptali sağlanacaktır. Satılan aracın yerine yeni bir araç satın aldıysanız yeni poliçe talebi menüsünden talebinizi ayrıca iletiniz. İptal talebi ile birlikte yeni poliçe talebi iletmeniz halinde kasko poliçeniz için iptal yapılmadan araç değişikliği zeyilnamesi düzenlenebilecektir.'
                  : '* YK (yeni kayıt) plaka üzerinden düzenlenmiş olan poliçenizin plaka değişiklik zeyilinin yapılabilmesi için lütfen güncel araç ruhsatını yükleyiniz. Araç şase numarasını kontrol ediniz. Zeyil talebinizi sadece trafik veya kasko poliçesi üzerinden iletmeniz yeterlidir, aynı araca ait farklı poliçe var ise zeyilnameniz o poliçe için de tarafımızca düzenlenecektir.'}
              </Typography>
            </>
          ) : (
            <>
              <SLabelTitle>Gerekli Belgeler</SLabelTitle>
              <Typography className={globalStyles.documentDescription}>
                * Mevcut araçlarınız veya ikinci el olarak satın alınan
                araçlarınız için belge seri numarası görünecek şekilde araç
                ruhsatı ekleyiniz.
              </Typography>
              <Typography className={globalStyles.documentDescription}>
                * Sıfır araçlarınız için araç faturasını ekleyiniz veya açıklama
                alanında aracın il plaka kodunu ve motor/şase numaralarını
                belirtiniz.
              </Typography>
            </>
          )}
        </Flex>
        <UploadButton
          fileName={[...values.upload_files]?.map((item) => item.name)}
          uploadRef={uploadInput}
          onChange={props.handleUploadFile}
        />
        <Flex justify="end">
          {touched.upload_files && errors.upload_files && (
            <ErrorMessage>{errors.upload_files.toString()}</ErrorMessage>
          )}
        </Flex>
        <Flex gap="small" vertical>
          <SLabelTitle>
            {props.type === modalTypes.ADDENDUM
              ? 'Yurtdışı Kasko Teminatı'
              : 'Açıklama'}
          </SLabelTitle>
          {props.type === modalTypes.ADDENDUM && (
            <Typography className={globalStyles.documentDescription}>
              * Yurtdışına çıkacak aracınızın mevcut kasko poliçesine belirli
              bir tarih aralığı için yurtdışı teminatı ekletmek için lütfen
              aracın ülkeden çıkış ve ülkeye dönüş tarihlerini aşağıda
              belirtiniz. Bu alandan sadece mevcut poliçeye yurtdışı kasko
              teminatı eklenebilmektedir. Yeşil Kart (yurtdışı trafik) sigortası
              için Yeni Poliçe Talebi butonu üzerinden ayrıca talep
              oluşturulmalıdır.
            </Typography>
          )}
          <STextArea
            placeholder={
              props.type === modalTypes.ADDENDUM
                ? 'Yurtdışı Kasko Teminatı'
                : 'Açıklama'
            }
            {...getFieldProps('description')}
          />
          {touched.description && errors.description && (
            <ErrorMessage>{errors.description}</ErrorMessage>
          )}
        </Flex>
        {props.type === modalTypes.EDIT && organization === 1 && (
          <Flex justify="space-between">
            <SLabelTitle>Durum Bilgisi</SLabelTitle>
            <StatusDropdown
              status={values.recent_status}
              statusChoices={props.statusChoices}
              onChange={(value) => setFieldValue('recent_status', value)}
            />
          </Flex>
        )}
        {props.type !== modalTypes.CANCEL &&
          props.type !== modalTypes.ADDENDUM && (
            <>
              <SLabelTitle>Poliçe Bilgileri</SLabelTitle>
              <Typography className={globalStyles.documentDescription}>
                * Bu alandaki bilgiler Siba Sigorta tarafından doldurulacaktır.
              </Typography>
              <SInput
                disabled={props.type === modalTypes.CREATE}
                placeholder="Poliçe No"
                {...getFieldProps('policy_number')}
              />
            </>
          )}
        {props.type !== modalTypes.CANCEL &&
          props.type !== modalTypes.ADDENDUM && (
            <Flex gap="small" vertical>
              <SLabelTitle>Tarih Aralıgı</SLabelTitle>
              <Flex justify="space-between" gap="small">
                <Flex vertical gap="small" className={globalStyles.widthHalf}>
                  <SDatePicker
                    id="start_date"
                    name="start_date"
                    disabled={props.type === modalTypes.CREATE}
                    value={
                      values.start_date
                        ? dayjs(values.start_date, DATE_FORMAT)
                        : null
                    }
                    placeholder="Başlangıç Tarihi"
                    onChange={(date) => {
                      if (date) {
                        setFieldValue('start_date', date.format(DATE_FORMAT));
                      }
                    }}
                  />
                  {touched.start_date && errors.start_date && (
                    <ErrorMessage>{errors.start_date}</ErrorMessage>
                  )}
                </Flex>
                <Flex vertical gap="small" className={globalStyles.widthHalf}>
                  <SDatePicker
                    id="end_date"
                    name="end_date"
                    disabled={props.type === modalTypes.CREATE}
                    value={
                      values.end_date
                        ? dayjs(values.end_date, DATE_FORMAT)
                        : null
                    }
                    placeholder="Bitiş Tarihi"
                    onChange={(date) => {
                      if (date) {
                        setFieldValue('end_date', date.format(DATE_FORMAT));
                      }
                    }}
                  />
                  {touched.end_date && errors.end_date && (
                    <ErrorMessage>{errors.end_date}</ErrorMessage>
                  )}
                </Flex>
              </Flex>
            </Flex>
          )}
        {props.type === modalTypes.EDIT && (
          <SMailSelector
            value={values.email_users}
            placeholder="Mail Listesi"
            options={props.withViewPerm?.map((wvp) => ({
              value: wvp.value,
              label: wvp.label,
            }))}
            onChange={(value) => setFieldValue('email_users', value)}
          />
        )}
      </Flex>
    </ModalDialog>
  );
};

export default MotorInsuranceModal;
